/**
 * Age badge for a maintenance request row (owner, 2026-09-26). Still-active
 * tickets are graded so red stays rare enough to catch the eye: under 48 h
 * neutral, 48 h–7 days amber, over 7 days red. A closed ticket shows how long
 * it took when its workflow recorded a close time, otherwise its age, in grey.
 */

export const ACTIVE_STATUSES = ["Open", "Scheduled", "Inprogress"];
const HOUR_MS = 36e5;
export const OVERDUE_HOURS = 48;
export const SEVERE_HOURS = 7 * 24;

/** Short duration: "35 min", "5 h", "3 d", "2 mo". */
export function formatDuration(hours) {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return "—";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 24) return `${Math.floor(hours)} h`;
  const days = hours / 24;
  if (days < 60) return `${Math.floor(days)} d`;
  return `${Math.floor(days / 30.44)} mo`;
}

/**
 * @returns {{ label: string, tone: "neutral"|"amber"|"red"|"closed", title: string }}
 */
export function ticketAge({ status, createdAt, closedAt }, now = new Date()) {
  const created = createdAt ? new Date(createdAt).getTime() : NaN;
  if (!Number.isFinite(created)) return { label: "—", tone: "neutral", title: "" };

  if (!ACTIVE_STATUSES.includes(status)) {
    const closed = closedAt ? new Date(closedAt).getTime() : NaN;
    if (Number.isFinite(closed) && closed >= created) {
      const took = formatDuration((closed - created) / HOUR_MS);
      return { label: `took ${took}`, tone: "closed", title: `Closed ${took} after it was submitted` };
    }
    const age = formatDuration((now.getTime() - created) / HOUR_MS);
    return { label: age, tone: "closed", title: `${status || "Closed"} · submitted ${age} ago` };
  }

  const hours = (now.getTime() - created) / HOUR_MS;
  const label = formatDuration(hours);
  if (hours > SEVERE_HOURS) return { label, tone: "red", title: `Waiting ${label} — over 7 days` };
  if (hours > OVERDUE_HOURS) return { label, tone: "amber", title: `Waiting ${label} — over 48 hours` };
  return { label, tone: "neutral", title: `Waiting ${label}` };
}
